import { useState } from 'react';
import styled, { css } from 'styled-components';
import { Box, Button, Heading, Interactive, Modal, Text } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { boxShadows, colors, spaces } from '@99designs/emu/settings';
import { IconTick2 } from '@99designs/icons';
import PulsingBox from '../../lib/components/PulsingBox';

const ImageOptions = styled.div`
    width: 696px;
    max-width: 100%;
    margin-bottom: ${spaces['2bu']};
    overflow-y: scroll;
    max-height: 60vh;

    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-gap: ${spaces['2bu']};

    ${mediaQuery.only('small')} {
        grid-template-columns: 1fr 1fr;
        grid-gap: ${spaces['1.5bu']};
    }
`;

const ImageButton = styled(Interactive)<{ $isSelected: boolean }>`
    position: relative;
    height: 0;
    width: 100%;
    padding-bottom: 100%;

    box-shadow: ${boxShadows.low};
    border: 3px solid transparent;

    ${({ $isSelected }) =>
        $isSelected &&
        css`
            border-color: ${colors.forrestLyf};
        `}

    svg {
        position: absolute;
        right: ${spaces['0.75bu']};
        bottom: ${spaces['0.75bu']};
    }
`;

const ImageBox = styled(Box)`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: ${spaces['2bu']};

    ${mediaQuery.only('small')} {
        padding: ${spaces['1.5bu']};
    }

    img {
        width: 100%;
    }
`;

// not sure if this is correct
const LogoSelectorSkeletonImage = styled(PulsingBox).attrs(() => ({
    height: ['150px', '202px', '214px'],
}))``;

const LogoSelectorSkeletonHeading = styled(PulsingBox)`
    height: 25px;
`;

export function LogoSelectorItemSkeleton() {
    return (
        <Box
            color={colors.grey800}
            paddingY="1bu"
            paddingX="none"
            data-testid="logo-selector-skeleton"
        >
            <LogoSelectorSkeletonImage />
            <LogoSelectorSkeletonHeading marginTop={['0.5bu', '1bu', '1bu']} />
        </Box>
    );
}

interface LogoSelectorModalProps {
    options: {
        name: string;
        previewUrl: string;
    }[];
    onSelected: (imageUrl?: string) => void;
    initiallySelectedUrl?: string;
}

export default function PrintRecommendationsLogoSelectorModal({
    options,
    onSelected,
    initiallySelectedUrl,
}: LogoSelectorModalProps) {
    const [selectedImageUrl, setSelectedImageUrl] = useState(initiallySelectedUrl);
    const [imagesLoaded, setImagesLoaded] = useState<string[]>([]);

    return (
        <Modal
            visible
            onClose={() => {
                onSelected();
            }}
            aria-label={__('Select a logo')}
        >
            <Box marginBottom={['0.75bu', '1.5bu', '1.5bu']}>
                <Box marginBottom="0.5bu">
                    <Heading accessibilityLevel={3}>{__('See how it looks')}</Heading>
                </Box>
                <Text>{__('Select a logo to see it on business cards, t-shirts and swag.')}</Text>
            </Box>
            <ImageOptions>
                {options.map((option) => (
                    <Box maxWidth="208px" width="100%" key={option.previewUrl}>
                        {!imagesLoaded.includes(option.previewUrl) && <LogoSelectorItemSkeleton />}
                        <Box display={imagesLoaded.includes(option.previewUrl) ? 'block' : 'none'}>
                            <ImageButton
                                onClick={() => {
                                    setSelectedImageUrl(option.previewUrl);
                                }}
                                $isSelected={option.previewUrl === selectedImageUrl}
                            >
                                <ImageBox>
                                    <img
                                        onLoad={() =>
                                            setImagesLoaded((i) => [...i, option.previewUrl])
                                        }
                                        src={option.previewUrl}
                                        alt=""
                                    />
                                </ImageBox>
                                {option.previewUrl === selectedImageUrl && (
                                    <IconTick2 color={colors.forrestLyf} />
                                )}
                            </ImageButton>
                            <Box marginTop={['0.5bu', '1bu', '1bu']}>
                                <Text as="div" align="center">
                                    {option.name}
                                </Text>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </ImageOptions>
            <Box display="flex" justifyContent="flex-end">
                <Button
                    width={['fill', 'auto', 'auto']}
                    onClick={() => {
                        onSelected(selectedImageUrl);
                    }}
                >
                    {__('Select logo')}
                </Button>
            </Box>
        </Modal>
    );
}
